import { motion } from "framer-motion";

const G = "#D8BB86";
const draw = (d = 0, dur = 1.6) => ({
  initial: { pathLength: 0, opacity: 0 },
  animate: { pathLength: 1, opacity: 1 },
  transition: { pathLength: { duration: dur, delay: d, ease: [0.22, 1, 0.36, 1] }, opacity: { duration: 0.3, delay: d } },
});
const loop = (d = 0, dur = 3) => ({ repeat: Infinity, duration: dur, delay: d, ease: "easeInOut" });

const Pathways = () => {
  const paths = ["M30 70 C150 80 230 170 320 200", "M30 200 L320 200", "M30 330 C150 320 230 230 320 200", "M80 30 C170 80 250 160 320 200", "M80 370 C170 320 250 240 320 200"];
  return (
    <g fill="none" stroke={G} strokeWidth="1.2">
      {paths.map((p, i) => (
        <motion.path key={p} d={p} {...draw(i * 0.12)} strokeOpacity={i === 1 ? 1 : 0.55} />
      ))}
      {[70, 200, 330].map((y) => <circle key={y} cx="30" cy={y} r="3" fill={G} />)}
      <motion.circle cx="320" cy="200" r="24" stroke={G} animate={{ r: [10, 34], opacity: [0.8, 0] }} transition={loop(1, 2.6)} />
      <circle cx="320" cy="200" r="7" fill="#F3D9A6" />
    </g>
  );
};

const NODES = [[70, 90], [150, 60], [240, 110], [330, 80], [110, 190], [200, 200], [300, 190], [60, 300], [160, 290], [250, 320], [340, 290], [200, 130], [120, 360], [290, 360]];
const EDGES = [[0, 1], [1, 11], [11, 2], [2, 3], [0, 4], [4, 5], [5, 11], [5, 6], [6, 3], [4, 7], [7, 8], [8, 5], [8, 9], [9, 6], [9, 10], [6, 10], [8, 12], [9, 13]];
const Constellation = () => (
  <g>
    {EDGES.map(([a, b], i) => (
      <motion.line key={i} x1={NODES[a][0]} y1={NODES[a][1]} x2={NODES[b][0]} y2={NODES[b][1]} stroke={G} strokeOpacity="0.4" strokeWidth="1" {...draw(i * 0.05, 1)} />
    ))}
    {NODES.map(([x, y], i) => (
      <motion.circle key={i} cx={x} cy={y} r={i === 5 ? 6 : 3} fill={i === 5 ? "#F3D9A6" : G} animate={{ opacity: [0.4, 1, 0.4] }} transition={loop(i * 0.2, 2.8)} />
    ))}
  </g>
);

const Ascending = () => (
  <g>
    {[0, 1, 2, 3, 4, 5].map((i) => {
      const h = 50 + i * 44;
      return (
        <motion.g key={i} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ duration: 1.2, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }} style={{ transformOrigin: `0px 350px` }}>
          <rect x={52 + i * 50} y={350 - h} width="30" height={h} fill="url(#m-gold)" fillOpacity={0.15 + i * 0.07} stroke={G} strokeOpacity="0.7" />
          <path d={`M${52 + i * 50} ${350 - h} l10 -10 h30 l-10 10z`} fill={G} fillOpacity="0.35" />
        </motion.g>
      );
    })}
    <motion.path d="M67 290 L117 246 L167 202 L217 158 L267 114 L317 70" fill="none" stroke="#F3D9A6" strokeDasharray="4 6" {...draw(0.8, 1.4)} />
  </g>
);

const Crest = () => (
  <g fill="none" stroke={G}>
    <line x1="200" y1="30" x2="200" y2="370" strokeOpacity="0.2" />
    <line x1="30" y1="200" x2="370" y2="200" strokeOpacity="0.2" />
    {[150, 115, 80, 45].map((s, i) => (
      <motion.rect key={s} x={200 - s / 2} y={200 - s / 2} width={s} height={s} strokeOpacity={0.3 + i * 0.18} style={{ transformOrigin: "200px 200px" }} initial={{ rotate: 45 }} animate={{ rotate: i % 2 ? [45, 135] : [45, -45] }} transition={{ duration: 18 + i * 4, repeat: Infinity, ease: "linear" }} />
    ))}
    <motion.circle cx="200" cy="200" r="120" strokeOpacity="0.5" strokeDasharray="2 8" {...draw(0, 2)} />
    <circle cx="200" cy="200" r="8" fill="#F3D9A6" stroke="none" />
  </g>
);

const Bridge = () => (
  <g fill="none" stroke={G}>
    <rect x="50" y="150" width="14" height="200" fill={G} fillOpacity="0.2" />
    <rect x="336" y="150" width="14" height="200" fill={G} fillOpacity="0.2" />
    {[60, 95, 130].map((h, i) => (
      <motion.path key={h} d={`M57 150 Q200 ${150 - h * 1.6} 343 150`} strokeOpacity={0.9 - i * 0.25} {...draw(i * 0.2, 1.5)} />
    ))}
    {[100, 150, 200, 250, 300].map((x, i) => (
      <motion.line key={x} x1={x} y1="250" x2={x} y2={150 - Math.sin(((x - 57) / 286) * Math.PI) * 96} strokeOpacity="0.35" {...draw(0.8 + i * 0.08, 0.8)} />
    ))}
    <motion.line x1="40" y1="250" x2="360" y2="250" {...draw(0.3, 1.2)} />
  </g>
);

const wave = (amp, phase) => {
  let d = "M-400 200";
  for (let x = -400; x <= 800; x += 10) d += ` L${x} ${200 + amp * Math.sin((x / 200) * Math.PI * 2 + phase)}`;
  return d;
};
const Waves = () => (
  <g fill="none">
    <line x1="20" y1="200" x2="380" y2="200" stroke={G} strokeOpacity="0.25" />
    {[70, 45, 22].map((a, i) => (
      <motion.path key={a} d={wave(a, i)} stroke={i === 2 ? "#F3D9A6" : G} strokeOpacity={0.4 + i * 0.25} strokeWidth={1 + i * 0.4} animate={{ x: [0, -200] }} transition={{ duration: 9 + i * 3, repeat: Infinity, ease: "linear" }} />
    ))}
  </g>
);

export const METAPHORS = [Pathways, Constellation, Ascending, Crest, Bridge, Waves];

export const MetaphorStage = ({ index }) => {
  const M = METAPHORS[index];
  return (
    <svg viewBox="0 0 400 400" className="h-full w-full" data-testid={`metaphor-${index}`}>
      <defs>
        <linearGradient id="m-gold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F3D9A6" />
          <stop offset="100%" stopColor="#AB7F3E" stopOpacity="0" />
        </linearGradient>
      </defs>
      <M />
    </svg>
  );
};
